import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import firebase from '../config/firebase.config';
import _ from 'lodash';

const database = firebase.firestore();

const DEFAULT_PAID_LEAVE_ALLOCATION = 25;

/**
 * Custom hook to fetch and cache the company's current-year leave policy
 * from companyDatabase/{companyId}/leavePolicy/{year}. Mirrors
 * useWorkingSchedule's cache-first + onSnapshot pattern.
 *
 * @param {string} [overrideCompanyId] - When set (non-empty), read the policy for this company instead of the user's active company.
 * @returns {{ paidLeaveAllocation: number, loading: boolean }}
 */
export function useLeavePolicy(overrideCompanyId) {
  const userProfile = useSelector((state) => state.auth.profile);
  const activeCompanyId = !_.isEmpty(userProfile?.activeCompany)
    ? userProfile?.activeCompany?.companyId
    : null;
  const companyId =
    typeof overrideCompanyId === 'string' && overrideCompanyId.trim() !== ''
      ? overrideCompanyId.trim()
      : activeCompanyId;

  const [paidLeaveAllocation, setPaidLeaveAllocation] = useState(
    DEFAULT_PAID_LEAVE_ALLOCATION
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) {
      setPaidLeaveAllocation(DEFAULT_PAID_LEAVE_ALLOCATION);
      setLoading(false);
      return;
    }

    const currentYear = new Date().getFullYear();
    const cacheKey = `leavePolicy_${companyId}_${currentYear}`;
    const cachedValue = localStorage.getItem(cacheKey);

    if (cachedValue !== null) {
      setPaidLeaveAllocation(Number(cachedValue));
      setLoading(false);
    }

    const policyRef = database
      .collection('companyDatabase')
      .doc(companyId)
      .collection('leavePolicy')
      .doc(currentYear.toString());

    const unsubscribe = policyRef.onSnapshot(
      (doc) => {
        const allocation =
          doc.data()?.paidLeaveAllocation ?? DEFAULT_PAID_LEAVE_ALLOCATION;
        setPaidLeaveAllocation(allocation);
        localStorage.setItem(cacheKey, String(allocation));
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching leave policy:', error);
        if (cachedValue === null) {
          setPaidLeaveAllocation(DEFAULT_PAID_LEAVE_ALLOCATION);
        }
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [companyId]);

  return { paidLeaveAllocation, loading };
}
